import { Dialog, Transition } from "@headlessui/react";
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { useTranslation } from "react-i18next";
import { ZoomIn, ZoomOut } from "lucide-react";
import { XIcon } from "../../components/icons";
import {
  loadPdfDocument,
  renderPageOffscreen,
  renderScaleFor,
} from "./pdfPreview";

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;
/** Breathing room around the page inside the viewer, in CSS px. */
const VIEWER_PADDING = 48;

const clampZoom = (value) =>
  Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(value * 100) / 100));

export default function PrintCertificatePreview({
  isOpen,
  onClose,
  UploadedPdfGif,
  header,
  isPdf = false,
  testId = "print-certificate-preview-modal",
}) {
  const { t } = useTranslation();
  const [scale, setScale] = useState(1);
  const [showPercentage, setShowPercentage] = useState(false);
  const transformRef = useRef();

  const [page, setPage] = useState(null);
  const [pdfStatus, setPdfStatus] = useState("idle");
  const [fitScale, setFitScale] = useState(1);
  const [viewerNode, setViewerNode] = useState(null);
  const canvasRef = useRef(null);
  const scaleRef = useRef(scale);
  const anchorRef = useRef(null);
  const dragRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  scaleRef.current = scale;
  const cssScale = fitScale * scale;
  const baseViewport = page?.getViewport({ scale: 1 });
  const cssWidth = baseViewport ? Math.round(baseViewport.width * cssScale) : 0;
  const cssHeight = baseViewport
    ? Math.round(baseViewport.height * cssScale)
    : 0;
  const canPan =
    viewerNode &&
    (cssWidth + VIEWER_PADDING > viewerNode.clientWidth ||
      cssHeight + VIEWER_PADDING > viewerNode.clientHeight);

  useEffect(() => {
    if (!isOpen || !isPdf || !UploadedPdfGif) return undefined;
    let cancelled = false;
    setScale(1);
    setPage(null);
    setPdfStatus("loading");
    loadPdfDocument(UploadedPdfGif)
      .then((pdf) => pdf.getPage(1))
      .then((firstPage) => {
        if (cancelled) return;
        setPage(firstPage);
      })
      .catch((error) => {
        console.error("Error rendering PDF:", error);
        if (!cancelled) setPdfStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, isPdf, UploadedPdfGif, reloadKey]);

  // Fit the whole page in the viewer, and keep it fitted on resize.
  useEffect(() => {
    if (!viewerNode || !page) return undefined;
    const base = page.getViewport({ scale: 1 });
    const update = () => {
      const width = viewerNode.clientWidth - VIEWER_PADDING;
      const height = viewerNode.clientHeight - VIEWER_PADDING;
      if (width <= 0 || height <= 0) return;
      setFitScale(Math.min(width / base.width, height / base.height));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(viewerNode);
    return () => observer.disconnect();
  }, [viewerNode, page]);

  useEffect(() => {
    if (!page || !cssScale) return undefined;
    let cancelled = false;
    const task = renderPageOffscreen(page, renderScaleFor(page, cssScale));
    task.promise
      .then((offscreen) => {
        const canvas = canvasRef.current;
        if (cancelled || !canvas) return;
        canvas.width = offscreen.width;
        canvas.height = offscreen.height;
        canvas.getContext("2d").drawImage(offscreen, 0, 0);
        setPdfStatus("ready");
      })
      .catch((error) => {
        if (cancelled || error?.name === "RenderingCancelledException") return;
        console.error("Error rendering PDF:", error);
        setPdfStatus("error");
      });
    return () => {
      cancelled = true;
      task.cancel();
    };
  }, [page, cssScale]);

  const zoomTo = useCallback(
    (next, focus) => {
      const target = clampZoom(next);
      if (viewerNode) {
        const rect = viewerNode.getBoundingClientRect();
        const x = focus ? focus.x - rect.left : viewerNode.clientWidth / 2;
        const y = focus ? focus.y - rect.top : viewerNode.clientHeight / 2;
        anchorRef.current = {
          x,
          y,
          fx: (viewerNode.scrollLeft + x) / viewerNode.scrollWidth,
          fy: (viewerNode.scrollTop + y) / viewerNode.scrollHeight,
        };
      }
      scaleRef.current = target;
      setScale(target);
      setShowPercentage(true);
    },
    [viewerNode],
  );

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor || !viewerNode) return;
    anchorRef.current = null;
    viewerNode.scrollLeft = anchor.fx * viewerNode.scrollWidth - anchor.x;
    viewerNode.scrollTop = anchor.fy * viewerNode.scrollHeight - anchor.y;
  }, [cssScale, viewerNode]);

  // Ctrl/Cmd + wheel (and trackpad pinch) zooms instead of zooming the page.
  useEffect(() => {
    if (!viewerNode || !isPdf) return undefined;
    const onWheel = (event) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const factor = Math.exp(-event.deltaY * 0.0025);
      zoomTo(scaleRef.current * factor, { x: event.clientX, y: event.clientY });
    };
    viewerNode.addEventListener("wheel", onWheel, { passive: false });
    return () => viewerNode.removeEventListener("wheel", onWheel);
  }, [viewerNode, isPdf, zoomTo]);

  const handlePointerDown = (event) => {
    if (!canPan || event.button !== 0) return;
    dragRef.current = {
      x: event.clientX,
      y: event.clientY,
      left: viewerNode.scrollLeft,
      top: viewerNode.scrollTop,
    };
    setIsDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag) return;
    viewerNode.scrollLeft = drag.left - (event.clientX - drag.x);
    viewerNode.scrollTop = drag.top - (event.clientY - drag.y);
  };

  const endDrag = () => {
    dragRef.current = null;
    setIsDragging(false);
  };

  const handleKeyDown = (event) => {
    if (!isPdf || event.target.closest?.("input, textarea")) return;
    if (event.key === "+" || event.key === "=") {
      event.preventDefault();
      zoomTo(scale + ZOOM_STEP);
    } else if (event.key === "-" || event.key === "_") {
      event.preventDefault();
      zoomTo(scale - ZOOM_STEP);
    } else if (event.key === "0") {
      event.preventDefault();
      zoomTo(1);
    }
  };

  useEffect(() => {
    let timer;
    if (showPercentage) {
      timer = setTimeout(() => setShowPercentage(false), 1000);
    }
    return () => clearTimeout(timer);
  }, [showPercentage]);

  const zoomIn = () =>
    isPdf ? zoomTo(scale + ZOOM_STEP) : transformRef.current.zoomIn();
  const zoomOut = () =>
    isPdf ? zoomTo(scale - ZOOM_STEP) : transformRef.current.zoomOut();
  const resetZoom = () => {
    if (isPdf) {
      zoomTo(1);
    } else {
      transformRef.current.resetTransform();
      setShowPercentage(true);
    }
  };

  const controlButton =
    "p-2 rounded-full hover:bg-cfCardHover transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton";

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            id={`${testId}-backdrop`}
            className="fixed inset-0 bg-black/75 backdrop-blur-sm"
          />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                id={testId}
                onKeyDown={handleKeyDown}
                className="w-full max-w-6xl transform overflow-hidden rounded-lg bg-cfSecondary text-left align-middle shadow-xl transition-all"
              >
                <div className="relative">
                  {header ? (
                    <Dialog.Title className="sr-only">{header}</Dialog.Title>
                  ) : null}
                  <button
                    type="button"
                    id={`${testId}-close`}
                    onClick={onClose}
                    aria-label={t("workspace:close")}
                    className="absolute right-4 top-2 z-10 p-2 rounded-full bg-cfSecondary/80 hover:bg-cfCardHover transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton"
                  >
                    <XIcon size={20} className="inline-flex" aria-hidden />
                  </button>

                  <div className="h-[93vh] w-full relative">
                    {isPdf ? (
                      <div
                        ref={setViewerNode}
                        id={`${testId}-viewer`}
                        className={`h-full w-full overflow-auto bg-gray-100 select-none ${
                          canPan
                            ? isDragging
                              ? "cursor-grabbing"
                              : "cursor-grab"
                            : ""
                        }`}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={endDrag}
                        onPointerCancel={endDrag}
                      >
                        <div
                          className="flex min-h-full min-w-full w-max items-center justify-center"
                          style={{ padding: VIEWER_PADDING / 2 }}
                        >
                          <canvas
                            ref={canvasRef}
                            aria-label={header || undefined}
                            className={`bg-white shadow-lg ring-1 ring-black/5 transition-opacity duration-300 ${
                              pdfStatus === "ready"
                                ? "opacity-100"
                                : "opacity-0"
                            }`}
                            style={{ width: cssWidth, height: cssHeight }}
                          />
                        </div>
                        {pdfStatus === "loading" && (
                          <div
                            className="absolute inset-0 flex items-center justify-center"
                            aria-busy="true"
                          >
                            <div className="w-12 h-12 rounded-full border-4 border-gray-200 border-t-cfPrimaryCardHover animate-spin" />
                          </div>
                        )}
                        {pdfStatus === "error" && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center">
                            <p className="text-gray-600">
                              {t("workspace:preview_load_failed")}
                            </p>
                            <button
                              type="button"
                              id={`${testId}-retry`}
                              onClick={() => setReloadKey((key) => key + 1)}
                              className="rounded-md bg-cfPrimaryButton px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-cfPrimaryButton"
                            >
                              {t("workspace:retry_preview")}
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <TransformWrapper
                        initialScale={1}
                        minScale={0.5}
                        maxScale={3}
                        wheel={{ step: 0.1 }}
                        onZoom={(ref) => {
                          setScale(ref.state.scale);
                          setShowPercentage(true);
                        }}
                        ref={transformRef}
                      >
                        {() => (
                          <TransformComponent
                            wrapperStyle={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                            contentStyle={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <button
                              className="border-none bg-transparent p-0 focus:outline-none"
                              onClick={() => {
                                if (scale !== 1) {
                                  transformRef.current.resetTransform();
                                  setScale(1);
                                  setShowPercentage(true);
                                }
                              }}
                            >
                              <img
                                src={UploadedPdfGif}
                                alt="Document preview"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                  objectFit: "contain",
                                }}
                              />
                            </button>
                          </TransformComponent>
                        )}
                      </TransformWrapper>
                    )}

                    {showPercentage && (
                      <div className="pointer-events-none absolute bottom-20 left-1/2 transform -translate-x-1/2 bg-black/50 text-cfSecondary px-3 py-1 rounded-md text-sm transition-opacity duration-300">
                        {Math.round(scale * 100)}%
                      </div>
                    )}

                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center space-x-2 bg-cfSecondary rounded-full p-2 shadow-lg">
                      <button
                        type="button"
                        id={`${testId}-zoom-out`}
                        onClick={zoomOut}
                        disabled={isPdf && (scale <= MIN_ZOOM || !page)}
                        className={controlButton}
                        title={t("workspace:zoom_out")}
                        aria-label={t("workspace:zoom_out")}
                      >
                        <ZoomOut className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        id={`${testId}-reset-zoom`}
                        onClick={resetZoom}
                        disabled={isPdf && !page}
                        className={`${controlButton} min-w-[3.5rem] text-sm tabular-nums`}
                        title={t("workspace:fit_to_screen")}
                        aria-label={t("workspace:fit_to_screen")}
                      >
                        {Math.round(scale * 100)}%
                      </button>
                      <button
                        type="button"
                        id={`${testId}-zoom-in`}
                        onClick={zoomIn}
                        disabled={isPdf && (scale >= MAX_ZOOM || !page)}
                        className={controlButton}
                        title={t("workspace:zoom_in")}
                        aria-label={t("workspace:zoom_in")}
                      >
                        <ZoomIn className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
